import {
  fetchAircraftStatus,
  isMissingTrainingBookingColumnsError,
  getCurrentOrganisationId,
  isMissingEndColumnsError,
  runBookingsQueryWithTimeOrderFallback,
} from './bookingService';
import {
  getBookingEndDate,
  getBookingStartDate,
  normalizeClockValue,
  parseDateTime,
  bookingUpdateContainsId,
} from './bookingUtils';

export function isAnotherDayReturnDateValid(departureDate, arrivalDate) {
  return Boolean(
    departureDate &&
    arrivalDate &&
    arrivalDate > departureDate
  );
}

export function bookingRangesOverlap(requestedStart, requestedEnd, booking) {
  const existingStart = getBookingStartDate(booking);
  const existingEnd = getBookingEndDate(booking);

  return Boolean(
    existingStart &&
    existingEnd &&
    requestedStart < existingEnd &&
    requestedEnd > existingStart
  );
}

function failure(errorMessage) {
  return { ok: false, errorMessage };
}

export async function submitBooking({
  supabase,
  user,
  currentMemberId,
  form,
  editingBookingId,
  baseAirfield,
  confirmBooking,
  trainingOptions = {},
  canCreateTrainingBooking = false,
}) {
  if (!user) {
    return failure('You must be logged in to book an aircraft.');
  }

  if (!form.aircraft || !form.date || !form.time) {
    return failure('Please complete aircraft, start date, and start time.');
  }

  if (form.showEndDate && (!form.endDate || !form.endTime)) {
    return failure('For a multi-day flight, please choose both end date and end time.');
  }

  if (
    form.flightType === 'land-away' &&
    form.showEndDate &&
    !isAnotherDayReturnDateValid(form.date, form.endDate)
  ) {
    return failure('For Another day, arrival date must be after the departure date.');
  }

  if (!form.showEndDate && !form.endTime) {
    return failure('Please complete end time.');
  }

  const aircraftId = Number(form.aircraft);
  const requestedEndDate = form.showEndDate ? (form.endDate || form.date) : form.date;
  const requestedStart = parseDateTime(form.date, form.time);
  const requestedEnd = parseDateTime(requestedEndDate, form.endTime);

  if (!Number.isFinite(aircraftId) || !requestedStart || !requestedEnd || requestedEnd <= requestedStart) {
    return failure('Please choose a valid aircraft and booking range.');
  }

  if (requestedStart < new Date()) {
    return failure('You cannot create or edit a booking with a start time in the past. Please choose a future date and time.');
  }

  const isTraining = form.bookingType === 'training';
  let studentProfile = null;
  let instructorProfile = null;
  let selectedEnrolment = null;

  if (isTraining) {
    if (!canCreateTrainingBooking) {
      return failure('Only same-organisation active instructors and owners/admins may create training bookings.');
    }

    studentProfile = (trainingOptions.studentProfiles || []).find(
      (profile) => String(profile.user_id) === String(form.studentUserId)
        && profile.status === 'active'
    );
    instructorProfile = (trainingOptions.instructorProfiles || []).find(
      (profile) => String(profile.user_id) === String(form.instructorUserId)
        && profile.status === 'active'
    );
    if (!studentProfile || !instructorProfile) {
      return failure('Select an active same-organisation student and instructor.');
    }

    if (form.courseEnrolmentId) {
      selectedEnrolment = (trainingOptions.enrolments || []).find(
        (enrolment) => String(enrolment.id) === String(form.courseEnrolmentId)
          && String(enrolment.student_profile_id) === String(studentProfile.id)
          && enrolment.status === 'active'
      );
      if (!selectedEnrolment) {
        return failure('Select an active course enrolment for the chosen student.');
      }
    }
  }

  const duration = Math.round((requestedEnd.getTime() - requestedStart.getTime()) / 60000);
  const organisationId = await getCurrentOrganisationId(supabase);
  if (!organisationId) {
    return failure('Could not identify the current organisation.');
  }
  const { data: existingBookings, error: conflictError } = await runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
    const trainingColumns = isTraining
      ? 'organisation_id, user_id, booking_type, student_user_id, instructor_user_id, '
      : '';
    let query = supabase
      .from('bookings')
      .select(`id, aircraft_id, date, time, end_date, end_time, duration, ${trainingColumns}aircraft!bookings_organisation_aircraft_fkey!inner(organisation_id)`)
      .eq('aircraft.organisation_id', organisationId)
      .order('date', { ascending: true });

    if (!isTraining) query = query.eq('aircraft_id', aircraftId);

    if (includeTimeOrder) {
      query = query.order('time', { ascending: true });
    }

    return query;
  });

  if (conflictError) {
    if (isTraining && isMissingTrainingBookingColumnsError(conflictError)) {
      return failure('Training bookings are unavailable until the Phase 2 database migration is applied. No booking was created.');
    }
    return failure('Could not check availability.');
  }

  const existingTrainingBooking = isTraining && editingBookingId
    ? (existingBookings || []).find((booking) => String(booking.id) === String(editingBookingId))
    : null;
  if (isTraining && editingBookingId && (
    !existingTrainingBooking?.user_id
    || !existingTrainingBooking?.aircraft_id
    || existingTrainingBooking.booking_type !== 'training'
    || String(existingTrainingBooking.organisation_id) !== String(organisationId)
    || String(existingTrainingBooking.aircraft?.organisation_id) !== String(organisationId)
  )) {
    return failure('Could not identify the existing same-organisation training booking. Please reload and try again.');
  }

  const overlaps = (existingBookings || []).some((booking) => {
    if (String(booking.aircraft_id) !== String(aircraftId)) return false;
    if (editingBookingId && String(booking.id) === String(editingBookingId)) {
      return false;
    }
    return bookingRangesOverlap(requestedStart, requestedEnd, booking);
  });

  if (overlaps) {
    return failure('This aircraft is already booked during that time. Please choose another slot.');
  }

  if (isTraining) {
    const participantOverlap = (existingBookings || []).some((booking) => {
      if (editingBookingId && String(booking.id) === String(editingBookingId)) return false;
      if (!bookingRangesOverlap(requestedStart, requestedEnd, booking)) return false;
      return String(booking.user_id) === String(form.studentUserId)
        || String(booking.user_id) === String(form.instructorUserId)
        || (booking.booking_type === 'training' && (
          String(booking.student_user_id) === String(form.studentUserId)
          || String(booking.instructor_user_id) === String(form.instructorUserId)
        ));
    });
    if (participantOverlap) {
      return failure('The selected student or instructor already has a booking during that time.');
    }
  }

  const { data: selectedAircraftStatus, error: selectedAircraftStatusError } =
    await fetchAircraftStatus(supabase, aircraftId);

  if (selectedAircraftStatusError) {
    return failure('Could not check aircraft status. Please try again.');
  }

  const aircraftCurrentStatus = String(selectedAircraftStatus?.status || 'serviceable').trim().toLowerCase();
  const aircraftRegistration = selectedAircraftStatus?.registration || 'This aircraft';

  if (aircraftCurrentStatus === 'aog') {
    return failure(`${aircraftRegistration} is AOG and cannot be booked.`);
  }

  if (aircraftCurrentStatus === 'defect' || aircraftCurrentStatus === 'deferred') {
    const warningConfirmed = confirmBooking(
      `${aircraftRegistration} is currently ${aircraftCurrentStatus.toUpperCase()}.\n\nYou may continue, but please review the aircraft status and open defects before flying.\n\nContinue with booking?`
    );

    if (!warningConfirmed) {
      return failure('Booking cancelled.');
    }
  }

  const flightType =
    form.flightType === 'local' || form.flightType === 'circuits'
      ? form.flightType
      : 'land-away';
  const departureAirfield =
    flightType === 'land-away'
      ? String(form.departureAirfield || baseAirfield || '').trim().toUpperCase()
      : String(baseAirfield || form.departureAirfield || '').trim().toUpperCase();
  const destinationAirfield =
    flightType === 'land-away'
      ? String(form.destinationAirfield || '').trim().toUpperCase()
      : String(baseAirfield || form.destinationAirfield || '').trim().toUpperCase();

  const bookingData = {
    user_id: isTraining ? form.studentUserId : user.id,
    aircraft_id: aircraftId,
    date: form.date,
    time: normalizeClockValue(form.time),
    end_date: requestedEndDate,
    end_time: normalizeClockValue(form.endTime),
    duration,
    notes: form.notes || null,
    briefing_status: 'not_started',
    briefing_data: {
      flightType,
      departureAirfield,
      destinationAirfield,
    },
    briefing_updated_at: null,
    planning_status: 'not_started',
    planning_data: {},
    planning_updated_at: null,
    organisation_id: organisationId,
    booking_type: isTraining ? 'training' : 'normal',
    student_user_id: isTraining ? form.studentUserId : null,
    instructor_user_id: isTraining ? form.instructorUserId : null,
    course_enrolment_id: isTraining ? (selectedEnrolment?.id || null) : null,
  };
  if (!editingBookingId) {
    if (isTraining && !currentMemberId) {
      return failure('Could not identify your Wingit member profile for this training booking.');
    }
    bookingData.created_by_user_id = isTraining ? currentMemberId : user.id;
  }
  const bookingDataPhase2Legacy = { ...bookingData };
  delete bookingDataPhase2Legacy.end_date;
  delete bookingDataPhase2Legacy.end_time;
  const bookingDataLegacy = {
    user_id: bookingData.user_id,
    aircraft_id: bookingData.aircraft_id,
    date: bookingData.date,
    time: bookingData.time,
    duration: bookingData.duration,
    notes: bookingData.notes,
    briefing_status: bookingData.briefing_status,
    briefing_data: bookingData.briefing_data,
    briefing_updated_at: bookingData.briefing_updated_at,
  };

  async function writeBooking(payload) {
    if (editingBookingId) {
      let query = supabase
        .from('bookings')
        .update(payload)
        .eq('id', editingBookingId)
        .eq('user_id', isTraining ? existingTrainingBooking.user_id : user.id);
      if (isTraining) {
        query = query
          .eq('organisation_id', existingTrainingBooking.organisation_id)
          .eq('booking_type', 'training')
          .eq('aircraft_id', existingTrainingBooking.aircraft_id);
      }
      return query.select('id').single();
    }
    return supabase.from('bookings').insert([payload]).select('id').single();
  }

  let { data, error } = await writeBooking(bookingData);

  if (error && isTraining && isMissingTrainingBookingColumnsError(error)) {
    return failure('Training bookings are unavailable until the Phase 2 database migration is applied. No booking was created.');
  }

  if (error && !isTraining && isMissingTrainingBookingColumnsError(error)) {
    ({ data, error } = await writeBooking(bookingDataLegacy));
  } else if (error && !isTraining && isMissingEndColumnsError(error)) {
    ({ data, error } = await writeBooking(bookingDataPhase2Legacy));
    if (error && isMissingTrainingBookingColumnsError(error)) {
      ({ data, error } = await writeBooking(bookingDataLegacy));
    }
  }

  if (error) {
    return failure(`Booking failed: ${error.message}`);
  }

  if (editingBookingId && !bookingUpdateContainsId(data, editingBookingId)) {
    return failure('Booking update could not be confirmed. Please reload and try again.');
  }

  return {
    ok: true,
    bookingId: data?.id || '',
    successMessage: editingBookingId ? 'Booking updated successfully.' : 'Booking created successfully.',
  };
}